import { makeApiPartners } from 'src/http/api/internal/internal-http';
import { RedeemsRepository } from 'src/repositories/redeems/redeems.repository';
import { Redeem } from 'src/types/redeems';
import { money } from 'src/utils/utils';
import { create } from 'zustand';

type FilteredRedeem = {
  _id: string;
  name: string;
  [key: string]: any;
};

type FilterRedeemsResult = {
  data: FilteredRedeem[];
  total: number;
  page: number;
  limit: number;
};

type States = {
  loadingPage: boolean;
  redeems: Redeem[];
  loadingFilteredRedeems: boolean;
  filteredRedeems: FilteredRedeem[];
  filteredRedeemsTotal: number;
  filteredRedeemsLimit: number;
};

type Actions = {
  setLoadingPage: (loading: boolean) => void;
  fetchRedeems: () => Promise<any>;
  findRedeems: (onlyNoGiftback?: boolean) => Promise<Redeem[] | null>;
  filterRedeems: (params: {
    partners: string[];
    name?: string;
    page?: number;
    limit?: number;
  }) => Promise<FilterRedeemsResult | null>;
  resetFilteredRedeems: () => void;
  syncGiffty: () => Promise<void>;
};

type RedeemsStore = States & Actions;

const initialState = {
  loadingPage: false,
  redeems: [],
  loadingFilteredRedeems: false,
  filteredRedeems: [],
  filteredRedeemsTotal: 0,
  filteredRedeemsLimit: 10,
};

const repository = new RedeemsRepository(makeApiPartners());

const parseFilteredRedeemsPayload = (
  payload: any,
  fallbackPage: number,
  fallbackLimit: number
): FilterRedeemsResult => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.data ||
      payload?.docs ||
      payload?.items ||
      payload?.results ||
      payload?.redeems ||
      [];

  const data = (list || []).map((item: any) => ({
    ...item,
    _id: item?._id || item?.id || '',
    name: item?.name || item?.details?.name || '',
  }));

  const total = Number(
    payload?.total ??
      payload?.totalDocs ??
      payload?.count ??
      payload?.totalFiltered ??
      payload?.meta?.total ??
      data.length
  );

  const page = Number(payload?.page ?? payload?.currentPage ?? fallbackPage);
  const limit = Number(payload?.limit ?? payload?.pageSize ?? fallbackLimit);

  return {
    data,
    total,
    page,
    limit,
  };
};

export const useRedeemsStore = create<RedeemsStore>((set) => ({
  ...initialState,
  setLoadingPage: (loading) => set({ loadingPage: loading }),
  resetFilteredRedeems: () =>
    set({
      filteredRedeems: [],
      filteredRedeemsTotal: 0,
      filteredRedeemsLimit: 10,
    }),

  filterRedeems: async ({ partners, name = '', page = 1, limit = 10 }) => {
    if (!partners?.length) {
      const emptyResult = {
        data: [],
        total: 0,
        page,
        limit,
      };
      set({
        loadingFilteredRedeems: false,
        filteredRedeems: [],
        filteredRedeemsTotal: 0,
        filteredRedeemsLimit: limit,
      });
      return emptyResult;
    }

    set({ loadingFilteredRedeems: true });
    const response = await repository.filterRedeems({
      partners,
      name,
      page,
      limit,
    });

    if (response.isLeft()) {
      set({
        loadingFilteredRedeems: false,
        filteredRedeems: [],
        filteredRedeemsTotal: 0,
        filteredRedeemsLimit: limit,
      });
      return null;
    }

    const parsed = parseFilteredRedeemsPayload(response.value, page, limit);

    set({
      loadingFilteredRedeems: false,
      filteredRedeems: parsed.data,
      filteredRedeemsTotal: parsed.total,
      filteredRedeemsLimit: parsed.limit,
    });

    return parsed;
  },

  fetchRedeems: async () => {
    set({ loadingPage: true });
    const response = await repository.getRedeems();
    if (response.isLeft()) {
      set({ loadingPage: false, redeems: [] });
      return null;
    }
    const { value } = response;
    const data = value?.map((v: any) => ({
      name: v.details?.name || '',
      price: money(v.details?.price) || '',
      status: v.status || '',
      partner: v.partner?.name || 'Sem parceiro vinculado',
      company: v.company?.name || 'Sem empresa vinculada',
      _id: v._id,
    }));

    set({ loadingPage: false, redeems: data });
    return value;
  },

  findRedeems: async (onlyNoGiftback = false) => {
    const response = await repository.getRedeems();
    if (response.isLeft()) {
      return null;
    }
    return (
      response.value?.filter(
        (v: any) => v.status === 'active' && (!onlyNoGiftback || v.origin !== 'giftback')
      ) || []
    );
  },

  syncGiffty: async () => {
    const response = await repository.syncGiffty();
    if (response.isLeft()) {
      throw response.value;
    }
  },
}));
